import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { CuratedPromptCard } from "@/components/curated-prompt-card";
import { getAllPrompts } from "@/lib/prompts";

interface TagSearch {
  page?: number;
}

// Helper function to format tag for display
function formatTagName(tag: string): string {
  return tag
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Helper function to generate tag descriptions
function getTagDescription(tag: string): string {
  const descriptions: Record<string, string> = {
    ecommerce:
      "AI prompts for ecommerce product images, online store visuals, and retail photography. Perfect for creating professional product listings.",
    "product-photography":
      "Professional product photography AI prompts for commercial use. Create catalog-ready images with perfect lighting and composition.",
    summer:
      "Summer-themed AI image prompts featuring bright, sunny, and seasonal visuals. Ideal for summer campaigns and seasonal content.",
    promotional:
      "Promotional and marketing AI image prompts for campaigns, advertisements, and brand materials. Create eye-catching promotional content.",
    youtube:
      "YouTube content AI prompts for video thumbnails, channel art, and video graphics. Optimize your YouTube presence with compelling visuals.",
    thumbnail:
      "Thumbnail AI prompts for video content, blog posts, and social media. Create click-worthy thumbnails that drive engagement.",
    business:
      "Business and professional AI image prompts for corporate content, presentations, and marketing materials.",
    flatlay:
      "Flatlay photography AI prompts for top-down product shots, lifestyle content, and Instagram-worthy compositions.",
    workspace:
      "Workspace and office environment AI prompts for productivity content, business blogs, and professional settings.",
    instagram:
      "Instagram-optimized AI prompts for social media content, posts, and stories. Create scroll-stopping Instagram visuals.",
    lifestyle:
      "Lifestyle photography AI prompts for authentic, everyday moments and relatable imagery. Perfect for lifestyle brands.",
    "food-photography":
      "Food photography AI prompts for restaurant menus, food blogs, and culinary content. Make your dishes look irresistible.",
    restaurant:
      "Restaurant and dining AI prompts for menu photography, ambiance shots, and culinary presentations.",
    gourmet:
      "Gourmet and fine dining AI prompts for high-end culinary imagery. Create sophisticated food photography.",
    commercial:
      "Commercial photography AI prompts for advertising, marketing, and professional brand imagery.",
  };

  return (
    descriptions[tag] ||
    `AI image generation prompts tagged with ${formatTagName(tag)}. Browse creative examples and copy-paste ready prompts for your projects.`
  );
}

export const Route = createFileRoute("/prompts/tag/$tag")({
  validateSearch: (search: Record<string, unknown>): TagSearch => {
    return {
      page: Number(search?.page) || 1,
    };
  },

  // SSR loader for tag prompts
  loader: async ({ params }) => {
    try {
      const allPrompts = await getAllPrompts({ data: undefined });
      const tagPrompts = allPrompts.filter((p) => p.tags.includes(params.tag));

      return {
        prompts: tagPrompts,
        tag: params.tag,
      };
    } catch (error) {
      console.error("[tag loader] Error:", error);
      return {
        prompts: [],
        tag: params.tag,
      };
    }
  },

  // SEO meta tags for tag pages
  head: ({ loaderData }) => {
    const { tag, prompts } = loaderData;
    const tagName = formatTagName(tag);
    const tagDescription = getTagDescription(tag);

    return {
      meta: [
        {
          title: `${tagName} AI Prompts - Creative Examples & Templates | Images AI`,
        },
        {
          name: "description",
          content: tagDescription,
        },
        {
          name: "keywords",
          content: `${tag} AI prompts, ${tag} image generation, AI ${tag} examples, FLUX 3 ${tag} prompts, GPT Image 2.5 ${tag}, Ideogram V4 ${tag}, ${tag} text to image`,
        },
        {
          property: "og:title",
          content: `${tagName} AI Prompts | Images AI`,
        },
        {
          property: "og:description",
          content: tagDescription,
        },
        {
          property: "og:type",
          content: "website",
        },
        {
          property: "og:url",
          content: `https://images-ai.app/prompts/tag/${tag}`,
        },
        {
          property: "twitter:card",
          content: "summary_large_image",
        },
        {
          property: "twitter:title",
          content: `${tagName} AI Prompts`,
        },
        {
          property: "twitter:description",
          content: tagDescription,
        },
      ],
      links: [
        {
          rel: "canonical",
          href: `https://images-ai.app/prompts/tag/${tag}`,
        },
      ],
      // JSON-LD structured data
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${tagName} AI Prompts`,
            description: tagDescription,
            url: `https://images-ai.app/prompts/tag/${tag}`,
            about: tag,
            publisher: {
              "@type": "Organization",
              name: "Images AI",
              url: "https://images-ai.app",
            },
            numberOfItems: prompts.length,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@id": "https://images-ai.app/",
                  name: "Home",
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@id": "https://images-ai.app/prompts",
                  name: "Prompts",
                },
              },
              {
                "@type": "ListItem",
                position: 3,
                item: {
                  "@id": `https://images-ai.app/prompts/tag/${tag}`,
                  name: tagName,
                },
              },
            ],
          }),
        },
      ],
    };
  },

  component: TagPage,
});

function TagPage() {
  const { prompts, tag } = Route.useLoaderData();
  const { session } = Route.useRouteContext();
  const tagName = formatTagName(tag);
  const tagDescription = getTagDescription(tag);

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb Navigation */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link
              to="/prompts"
              className="hover:text-foreground transition-colors"
            >
              Prompts
            </Link>
            <span>/</span>
            <span className="text-foreground font-medium">{tagName}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl font-bold">
              {tagName} AI Prompts
            </h1>
            <p className="text-lg text-muted-foreground">{tagDescription}</p>
            <div className="pt-4">
              {session ? (
                <Link to="/dashboard" preload="intent">
                  <Button size="lg" className="rounded-full px-8">
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/signup" preload="intent">
                  <Button size="lg" className="rounded-full px-8">
                    Try These Prompts Free
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Prompts Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {prompts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                No prompts with this tag yet. Check back soon!
              </p>
              <Link to="/prompts" className="inline-block mt-4">
                <Button variant="outline" className="rounded-full">
                  Browse All Prompts
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-sm text-muted-foreground">
                  {prompts.length} {prompts.length === 1 ? "prompt" : "prompts"}{" "}
                  found
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {prompts.map((prompt) => (
                  <CuratedPromptCard key={prompt.slug} prompt={prompt} />
                ))}
              </div>
            </>
          )}

          {/* CTA Section */}
          <div className="mt-20 text-center space-y-6">
            <h2 className="text-3xl font-bold">Ready to create your own?</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Generate with FLUX 3, GPT Image 2.5, Qwen Image 3, and Seedream 5.
              Free to start — no credit card required.
            </p>
            {session ? (
              <Link to="/dashboard">
                <Button size="lg" className="rounded-full px-12">
                  Go to Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/signup">
                <Button size="lg" className="rounded-full px-12">
                  Get Started Free
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
